import { useCallback, useEffect, useState } from "react"
import { MapPin, Play, Square, Loader2, AlertTriangle } from "lucide-react"
import { supabase } from "../lib/supabase"
import type { Property, TimeEntry } from "../lib/supabase"
import { useAuth } from "./AuthProvider"
import { capturePosition, checkGeofence, type GeoPosition, type GeofenceStatus } from "../lib/geolocation"
import { useToast } from "./Toast"

function useToastHelpers() {
  const { toast } = useToast()
  return {
    success: (msg: string) => toast(msg, "success"),
    error: (msg: string) => toast(msg, "error"),
  }
}

interface ClockInOutProps {
  className?: string
  /** Compact mode for sidebar embedding. */
  compact?: boolean
  onActiveShiftChange?: (active: boolean) => void
}

export function ClockInOut({ className = "", compact = false, onActiveShiftChange }: ClockInOutProps) {
  const { operatorId, role } = useAuth()
  const isOperator = role === "operator"
  const toast = useToastHelpers()

  const [properties, setProperties] = useState<Pick<Property, "id" | "name" | "latitude" | "longitude" | "geofence_radius_meters">[]>([])
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("")
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null)
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(false)
  const [elapsed, setElapsed] = useState("")
  const [geoStatus, setGeoStatus] = useState<GeofenceStatus | null>(null)

  useEffect(() => {
    if (!operatorId) { setLoading(false); return }

    const loadData = async () => {
      const [{ data: props }, { data: entries }] = await Promise.all([
        supabase.from("properties").select("id, name, latitude, longitude, geofence_radius_meters").order("name"),
        (supabase as any)
          .from("time_entries")
          .select("*")
          .eq("operator_id", operatorId)
          .is("clock_out", null)
          .order("clock_in", { ascending: false })
          .limit(1),
      ])
      setProperties(props ?? [])
      const active = entries?.[0] ?? null
      setActiveEntry(active as TimeEntry | null)
      if (active?.property_id) setSelectedPropertyId(active.property_id)
      onActiveShiftChange?.(!!active)
      setLoading(false)
    }
    loadData()
  }, [operatorId])

  useEffect(() => {
    if (!activeEntry) { setElapsed(""); return }
    const tick = () => {
      const ms = Date.now() - new Date(activeEntry.clock_in).getTime()
      const totalMin = Math.floor(ms / 60_000)
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      setElapsed(h > 0 ? `${h}h ${m}m` : `${m}m`)
    }
    tick()
    const id = setInterval(tick, 30_000)
    return () => clearInterval(id)
  }, [activeEntry])

  const handleClockIn = useCallback(async () => {
    if (!operatorId || !selectedPropertyId) return
    setActing(true)

    let pos: GeoPosition | null = null
    let fence: GeofenceStatus = "unknown"
    try {
      pos = await capturePosition()
      const prop = properties.find((p) => p.id === selectedPropertyId)
      if (prop) fence = checkGeofence(pos, prop)
      setGeoStatus(fence)
    } catch {
      setGeoStatus("unknown")
    }

    const { data, error } = await (supabase as any)
      .from("time_entries")
      .insert({
        operator_id: operatorId,
        property_id: selectedPropertyId,
        clock_in: new Date().toISOString(),
        clock_in_lat: pos?.lat ?? null,
        clock_in_lng: pos?.lng ?? null,
        clock_in_accuracy: pos?.accuracy ?? null,
        geofence_status_in: fence,
      })
      .select()
      .single()

    if (error) {
      toast.error("Failed to clock in")
      console.error(error)
    } else {
      setActiveEntry(data as TimeEntry)
      onActiveShiftChange?.(true)
      toast.success(fence === "outside" ? "Clocked in (outside geofence)" : "Clocked in")
    }
    setActing(false)
  }, [operatorId, selectedPropertyId, properties, toast, onActiveShiftChange])

  const handleClockOut = useCallback(async () => {
    if (!activeEntry) return
    setActing(true)

    let pos: GeoPosition | null = null
    let fence: GeofenceStatus = "unknown"
    try {
      pos = await capturePosition()
      const prop = properties.find((p) => p.id === activeEntry.property_id)
      if (prop) fence = checkGeofence(pos, prop)
      setGeoStatus(fence)
    } catch {
      setGeoStatus("unknown")
    }

    const { error } = await (supabase as any)
      .from("time_entries")
      .update({
        clock_out: new Date().toISOString(),
        clock_out_lat: pos?.lat ?? null,
        clock_out_lng: pos?.lng ?? null,
        clock_out_accuracy: pos?.accuracy ?? null,
        geofence_status_out: fence,
      })
      .eq("id", activeEntry.id)

    if (error) {
      toast.error("Failed to clock out")
      console.error(error)
    } else {
      setActiveEntry(null)
      setGeoStatus(null)
      onActiveShiftChange?.(false)
      toast.success("Clocked out")
    }
    setActing(false)
  }, [activeEntry, properties, toast, onActiveShiftChange])

  if (!isOperator || !operatorId) return null

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin text-white/30" />
      </div>
    )
  }

  const selectedProp = properties.find((p) => p.id === (activeEntry?.property_id ?? selectedPropertyId))

  if (compact) {
    return (
      <div className={`space-y-2 ${className}`}>
        {activeEntry ? (
          <button
            type="button"
            onClick={handleClockOut}
            disabled={acting}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/15 border border-red-400/20 text-red-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-red-500/25 transition-colors disabled:opacity-50"
          >
            {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
            <span className="flex-1 text-left truncate">{selectedProp?.name ?? "Shift"}</span>
            <span className="text-red-400/70">{elapsed}</span>
          </button>
        ) : (
          <>
            <select
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/70 font-mono text-[10px] focus:outline-none focus:ring-2 focus:ring-teal-400/30"
            >
              <option value="">Select property...</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleClockIn}
              disabled={acting || !selectedPropertyId}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/15 border border-emerald-400/20 text-emerald-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-500/25 transition-colors disabled:opacity-30"
            >
              {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Clock In
            </button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={`rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 space-y-4 ${className}`}>
      <div>
        <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-white/50">Time Tracking</p>
        <p className="font-mono text-[9px] text-teal-400/50 mt-0.5">clock.shift()</p>
      </div>

      {activeEntry ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/80 font-mono text-sm font-semibold truncate">{selectedProp?.name ?? "Active shift"}</p>
              <p className="text-white/40 font-mono text-[11px]">
                Started {new Date(activeEntry.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            <span className="font-mono text-lg font-bold text-teal-400 tabular-nums">{elapsed}</span>
          </div>

          {geoStatus === "outside" && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-400/20">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className="text-amber-400/80 font-mono text-[10px]">Outside geofence</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleClockOut}
            disabled={acting}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/15 border border-red-400/20 text-red-400 font-mono text-xs font-bold uppercase tracking-wider hover:bg-red-500/25 transition-colors disabled:opacity-50"
          >
            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
            Clock Out
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest block mb-1.5">
              <MapPin className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              Property
            </label>
            <select
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white/80 font-mono text-[12px] focus:outline-none focus:ring-2 focus:ring-teal-400/30"
            >
              <option value="">Select property...</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleClockIn}
            disabled={acting || !selectedPropertyId}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500/15 border border-emerald-400/20 text-emerald-400 font-mono text-xs font-bold uppercase tracking-wider hover:bg-emerald-500/25 transition-colors disabled:opacity-30"
          >
            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Clock In
          </button>
        </div>
      )}
    </div>
  )
}
